import React, { useEffect, useMemo, useRef, useState } from "react";
import SpeakerDistributionSvg from "./speaker-distribution.svg";

import styles from "./SpeakerDistributionDiagram.module.scss";

export type SpeakerDistributionItem =
  | "front-system-sub-bass"
  | "front-system"
  | "surround-system-sides"
  | "surround-system-back"
  | "ceiling-system-sub-bass"
  | "ceiling-system";

interface SpeakerDistributionDiagramProps {
  onItemSelect: (item: SpeakerDistributionItem) => void;
  activeItems: SpeakerDistributionItem[];
}

export const SpeakerDistributionDiagram: React.FC<SpeakerDistributionDiagramProps> = ({
  onItemSelect,
  activeItems,
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    setMounted(true);
  }, [divRef]);

  // memoize item handles
  const itemHandles = useMemo<Partial<Record<SpeakerDistributionItem, Element>>>(() => {
    if (!mounted) {
      return {};
    }

    return {
      "front-system-sub-bass": divRef.current!.querySelector(".front-system-sub-bass") ?? undefined,
      "front-system": divRef.current!.querySelector(".front-system") ?? undefined,
      "surround-system-sides": divRef.current!.querySelector(".surround-system-sides") ?? undefined,
      "surround-system-back": divRef.current!.querySelector(".surround-system-back") ?? undefined,
      "ceiling-system-sub-bass": divRef.current!.querySelector(".ceiling-system-sub-bass") ?? undefined,
      "ceiling-system": divRef.current!.querySelector(".ceiling-system") ?? undefined,
    };
  }, [divRef, mounted]);

  // setup click listeners
  useEffect(() => {
    const listeners: Record<string, () => void> = {};

    Object.entries(itemHandles).forEach(([id, handle]) => {
      handle.classList.add("item");
      listeners[id] = () => onItemSelect(id as SpeakerDistributionItem);
      handle.addEventListener("click", listeners[id]);
    });

    return () => {
      Object.entries(itemHandles).forEach(([id, handle]) => {
        handle.removeEventListener("click", listeners[id]);
      });
    };
  }, [mounted, itemHandles, onItemSelect]);

  // add active class based on props
  useEffect(() => {
    Object.entries(itemHandles).forEach(([id, handle]) => {
      if (activeItems?.includes(id as SpeakerDistributionItem)) {
        handle.classList.add("active");
      } else {
        handle.classList.remove("active");
      }
    });
  }, [itemHandles, activeItems]);

  return (
    <div ref={divRef} className={styles.root}>
      <SpeakerDistributionSvg />
    </div>
  );
};
